import { PassSimulationProvider } from "./components/PassPlanner/PassSimulationContext";
import {
  PassPlannerProvider,
  usePassPlanner,
} from "./components/PassPlanner/PassPlannerContext";
import {
  DRAWER_TRANSITION_MS,
  RIGHT_PANEL_WIDTH,
} from "./components/trackerLayout";
import { SatDataLoader } from "./components/SatDataLoader/SatDataLoader";
import { QueryClient, QueryClientProvider } from "react-query";
import { Box, CssBaseline, createTheme, ThemeProvider } from "@mui/material";
import { FloatingMenuButton } from "./components/FloatingMenuButton/FloatingMenuButton";
import { AppDrawer } from "./components/AppDrawer/AppDrawer";
import {
  AppSettingsProvider,
  useAppSettings,
} from "./context/AppSettingsContext";

export const queryClient = new QueryClient();

const createTrackerTheme = (mode: "light" | "dark") => {
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: { main: isDark ? "#6cdaa6" : "#187749" },
      background: {
        default: isDark ? "#101215" : "#f5f6f7",
        paper: isDark ? "#181b20" : "#ffffff",
      },
      text: {
        primary: isDark ? "#f0f2f4" : "#20252b",
        secondary: isDark ? "#9ba4af" : "#626c78",
      },
      divider: isDark ? "rgba(255,255,255,0.09)" : "rgba(20,30,40,0.1)",
    },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      button: { textTransform: "none", fontWeight: 600 },
      body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none", boxShadow: "none" },
        },
      },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiIconButton: {
        styleOverrides: {
          root: { minWidth: 44, minHeight: 44, borderRadius: 8 },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": {
              outline: `2px solid ${isDark ? "#6cdaa6" : "#187749"}`,
              outlineOffset: -2,
            },
          },
        },
      },
    },
  });
};

const lightTheme = createTrackerTheme("light");
const darkTheme = createTrackerTheme("dark");

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <AppSettingsProvider>
      <PassPlannerProvider>
        <PassSimulationProvider>
          <AppContent />
        </PassSimulationProvider>
      </PassPlannerProvider>
    </AppSettingsProvider>
  </QueryClientProvider>
);

const AppContent = () => {
  const { isDarkMode } = useAppSettings();
  const { open: plannerOpen, satellitesOpen } = usePassPlanner();
  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          height: "100dvh",
          width: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          pr: {
            xs: 0,
            md: plannerOpen || satellitesOpen ? `${RIGHT_PANEL_WIDTH}px` : 0,
          },
          transition: {
            xs: "none",
            md: theme.transitions.create("padding-right", {
              duration: DRAWER_TRANSITION_MS,
              easing: theme.transitions.easing.easeOut,
            }),
          },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        <FloatingMenuButton />
        <AppDrawer />
        <Box
          component="main"
          aria-label="Satellite tracker"
          sx={{ flex: 1, minHeight: 0, minWidth: 0, display: "flex" }}
        >
          <SatDataLoader />
        </Box>
      </Box>
    </ThemeProvider>
  );
};
